import * as React from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { useVerantwortlichkeiten } from "./useVerantwortlichkeiten";
import { useVerantwortlichkeitMatrix } from "./useVerantwortlichkeitMatrix";
import { VerantwortlichkeitDropdown } from "./VerantwortlichkeitDropdown";
import {
  baueRollenOptionen,
  ergaenzeBestandsRollen,
  leitungsOptionFuerBestand,
  optionsWert,
  LABEL_SONSTIGES,
  OPT_GES,
  OPT_GF,
  OPT_SONSTIGES,
  type RollenOption,
} from "./verantwortlichkeitKategorien";
import {
  abgeleiteterHaken,
  hakenVorschlagNeu,
  baueHakenJeRolle,
  menschImUnternehmenWert,
} from "./menschImUnternehmen";
import { istGfGesellschafter, LABEL_GF_GESELLSCHAFTER } from "../../utils/gfGesellschafter";
import { AnteilFeld } from "./AnteilFeld";
import { ContactRefField } from "../relationsInline/ContactRefField";
import type { ContactRef } from "../relationsInline/types";

/** Eine gewählte Verantwortlichkeit/Rolle (Mehrfachauswahl). */
export type VerantRole =
  | { kind: "gf" }
  | {
      kind: "gesellschafter";
      share?: string;
      istGeschaeftsfuehrer?: boolean;
      /**
       * Funktion der gespeicherten Gesellschafter-Zeile („Komplementärin
       * (Gesellschaft)", „Kommanditist" …), nur beim Laden aus dem Bestand gesetzt.
       * Wird NICHT geschrieben — sie dient allein der Erkennung der
       * Komplementär-GmbH (utils/komplementaerGmbh.ts, T11).
       */
      funktion?: string;
    }
  // `share` traegt die Quote der Rolle, sofern die Matrix eine vorsieht
  // (Kommanditist in Euro, Wohnungseigentuemer als Bruchteil, Aktionaer in
  // Prozent). Optional und additiv — bis 08/2026 gab es Anteile nur an
  // `gesellschafter`, und kein Aufrufer muss das Feld setzen.
  | { kind: "mensch"; verantwortlichkeit: string; share?: string }; // Katalog-Bezeichnung ODER Freitext

/** Wert der Komponente — ein Typ (intern/extern) + Mehrfachauswahl von Rollen. */
export type VerantwortlichkeitValue = {
  isExtern: boolean;
  roles: VerantRole[];
  /** Bei extern: EINE zugeordnete Firma (für alle extern-Rollen). */
  externalCompany?: ContactRef | null;
  /**
   * Der PO-Haken „Mensch im Unternehmen (bAV/bKV)" (15.08.2026). Er hängt an der
   * VERBINDUNG, nicht an einer Rolle, und ist der einzige Weg, einen Mitarbeiter
   * ohne jede Verantwortlichkeit zu erfassen — die Grundlage für bAV/bKV.
   *
   * Der Wert wird aus den Rollen abgeleitet und von der Komponente auch dann
   * gesetzt, wenn niemand die Checkbox anfasst.
   */
  istMenschImUnternehmen?: boolean;
  /**
   * Der Anwender hat gegen die Ableitung gesetzt → die Automatik fasst den Haken
   * nicht mehr an. Deckt sich seine Eingabe wieder mit der Ableitung, fällt das
   * Flag zurück auf `false`.
   */
  istMenschImUnternehmenManuell?: boolean;
  /**
   * Die private Assistenz dieser Person (PO 21.09.2026) — nur bei einer
   * Leitungs- oder Gesellschafterrolle, höchstens eine.
   *
   * Seit 10/2026 nicht mehr in dieser Eingabe erfasst: Die Assistenz ist eine
   * eigene Verantwortlichkeit (Karte „Persönlicher Assistent" der
   * Personenakte), kein Abschnitt in den Verantwortungs-Popups. Das Feld wird
   * nur noch durchgereicht, damit ein vorhandener Wert nicht verloren geht.
   *
   * Ein ganz gewöhnlicher Kontakt-Bezug: entweder ein bestehender Kontakt oder
   * ein frei eingetippter, der beim Speichern angelegt wird. Deshalb derselbe
   * Typ wie `externalCompany` — und dasselbe Eingabefeld.
   */
  assistenz?: ContactRef | null;
  /** Die bestehende Zuordnung lösen (nur die Verknüpfung, nicht den Menschen). */
  assistenzLoesen?: boolean;
};

/** Bezeichnung → DOM-taugliches id-Fragment („Teilhaber / Miteigentümer"). */
const slugFuerId = (s: string) =>
  s
    .toLowerCase()
    .replace(/[äöüß]/g, (c) => ({ "ä": "ae", "ö": "oe", "ü": "ue", "ß": "ss" })[c] ?? c)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const ACC = "#7d2169";
const INK = "#241522";
const MUTED = "#6b5a66";
const LINE = "#ddd3dc";

const useStyles = makeStyles({
  wrap: { display: "flex", flexDirection: "column", rowGap: "12px", width: "100%" },
  label: { fontSize: "0.78rem", fontWeight: 600, color: MUTED },
  hint: { fontSize: "0.72rem", color: MUTED, lineHeight: 1.4 },
  fieldLbl: { fontSize: "0.7rem", fontWeight: 600, color: MUTED, textTransform: "uppercase", letterSpacing: "0.04em" },
  seg: { display: "inline-flex", columnGap: "6px" },
  segBtn: {
    ...shorthands.border("1px", "solid", LINE),
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding("7px", "16px"),
    fontSize: "0.88rem",
    fontWeight: 600,
    color: INK,
    backgroundColor: "#fff",
    cursor: "pointer",
  },
  segActive: { backgroundColor: ACC, color: "#fff", ...shorthands.borderColor(ACC) },
  inset: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    columnGap: "10px",
    rowGap: "6px",
  },
  input: {
    ...shorthands.border("1px", "solid", LINE),
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding("7px", "10px"),
    fontSize: "0.86rem",
    color: INK,
    backgroundColor: "#fff",
    outlineStyle: "none",
    ":focus": { ...shorthands.borderColor(ACC), boxShadow: "0 0 0 3px rgba(125,33,105,0.13)" },
  },
  field: { display: "flex", flexDirection: "column", rowGap: "4px" },
  extWrap: { display: "flex", flexDirection: "column", rowGap: "6px" },
  // Hinweisbox „erst die Rechtsform" — auffällig genug, um nicht überlesen zu
  // werden, aber kein Fehler: In dieser Ausbaustufe blockiert sie nichts.
  warnBox: {
    display: "flex",
    columnGap: "8px",
    ...shorthands.border("1px", "solid", "#e8d7a8"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding("8px", "10px"),
    backgroundColor: "#fdf7e6",
    fontSize: "0.78rem",
    lineHeight: 1.45,
    color: "#6b4e07",
  },
  warnIcon: { flexShrink: 0, color: "#a5790a", display: "inline-flex", marginTop: "1px" },
  mitarbeitZeile: {
    display: "flex",
    alignItems: "flex-start",
    columnGap: "9px",
    alignSelf: "flex-start",
    cursor: "pointer",
    fontSize: "0.85rem",
    fontWeight: 600,
  },
  mitarbeitHinweis: {
    display: "block",
    fontSize: "0.76rem",
    fontWeight: 400,
    color: MUTED,
    marginTop: "1px",
  },
  abgeleitet: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "7px",
    alignSelf: "flex-start",
    ...shorthands.border("1px", "solid", "#e2cbdd"),
    ...shorthands.borderRadius("999px"),
    ...shorthands.padding("4px", "11px"),
    backgroundColor: "#f7f0f5",
    color: ACC,
    fontSize: "0.78rem",
    fontWeight: 600,
  },
});

const WarnIcon = (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M10 3.6l7 12.2H3l7-12.2z" />
    <path d="M10 8.4v3.4M10 14h.01" />
  </svg>
);

export function VerantwortlichkeitInput({
  value,
  onChange,
  idBase = "verant",
  hideExternalCompany,
  legalFormId,
  gegenkontaktIstFirma,
  neuerEintrag,
}: {
  value: VerantwortlichkeitValue | null;
  onChange: (v: VerantwortlichkeitValue) => void;
  idBase?: string;
  /** „Externe Firma"-Unter-Picker ausblenden (wenn die Firma anderswo gewählt wird). */
  hideExternalCompany?: boolean;
  /**
   * Rechtsform der Firma, um die es geht. Ist sie gesetzt, kommen die Organrollen
   * ausschließlich aus der Matrix `Rechtsform × Rolle` (harte Filterung,
   * PO 15.08.2026) — was dort nicht steht, ist hier nicht wählbar.
   *
   * Fehlt sie, kennt der Aufrufer sie nicht (die drei Bereinigungsseiten laden
   * sie nicht mit): Dann steht ein Hinweis über der Auswahl und es bleiben nur
   * Geschäftsführer und Gesellschafter als Rückfall.
   */
  legalFormId?: number;
  /**
   * Der Gegen-Kontakt ist selbst eine Firma. Dann sind nur die Rollen wählbar,
   * die eine Firma tragen kann (Komplementärin, Gesellschafterin, Kommanditistin
   * …), und der Typ-Umschalter entfällt — eine beteiligte Firma ist nie ein
   * extern beauftragter Dienstleister.
   *
   * Ersetzt `lockGesellschafterIntern`, das die Auswahl bis 08/2026 hart auf
   * „Gesellschafter, intern" normalisierte und damit die Komplementär-GmbH
   * unmöglich machte.
   */
  gegenkontaktIstFirma?: boolean;
  /**
   * Eine NEUE Verantwortung (Paket A8): Der Haken „Mensch im Unternehmen" ist
   * für interne Rollen vorbelegt, außer Gesellschafter, Aufsicht/Beirat und
   * Bevollmächtigter (menschImUnternehmen.ts `hakenVorschlagNeu`). Ein schon
   * beim Öffnen manuell gesetzter Haken (z. B. „+ Mensch im Unternehmen")
   * und jeder Klick auf den Haken haben Vorrang.
   */
  neuerEintrag?: boolean;
}) {
  const s = useStyles();
  const { items, loading: katalogLaedt } = useVerantwortlichkeiten();
  const { matrix, loading: matrixLaedt } = useVerantwortlichkeitMatrix(legalFormId);
  const loading = katalogLaedt || matrixLaedt;

  // Bei einer FIRMA als Gegenkontakt bleibt die Eingabe IMMER im Modus „intern".
  // `art` steuert nicht nur den Katalog, sondern auch, ob der Organrollen-Block
  // überhaupt gebaut wird (verantwortlichkeitKategorien.ts) und ob der
  // „Externe Firma"-Picker aufgeht. Der Backend-Guard schreibt für eine reine
  // Zuständigkeit trotzdem isExtern=true — beim Wiederöffnen käme das über
  // relsToVerant als `value.isExtern` herein, und die Organrollen fielen für
  // einen Frame aus der Liste. Welche Zuständigkeiten angeboten werden,
  // entscheidet stattdessen `zustaendigkeitsArt`.
  const art: "intern" | "extern" = !gegenkontaktIstFirma && value?.isExtern ? "extern" : "intern";
  const roles = value?.roles ?? [];

  // Der Typ-Umschalter fehlt bei einer Firma, ein aus dem Bestand geladenes
  // `isExtern` bliebe sonst unsichtbar stehen. Was extern/intern am Ende BEDEUTET,
  // entscheidet seit 20.08.2026 nicht mehr diese Maske, sondern der Guard im
  // Backend (menschenRollenGuard): eine reine Zuständigkeit einer Firma ist eine
  // Beauftragung, eine Organ-/Beteiligungsrolle nicht. Hier wird der Wert nur
  // sauber gehalten, damit `verantToItems` keine zweite externe Firma anhängt.
  React.useEffect(() => {
    if (!gegenkontaktIstFirma || !value?.isExtern) return;
    onChange({
      isExtern: false,
      roles: value?.roles ?? [],
      externalCompany: null,
      assistenz: value?.assistenz ?? null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gegenkontaktIstFirma, value?.isExtern]);

  const matrixBekannt = !!legalFormId && legalFormId > 0;
  // Die Matrix ist auch für Platzhalter-Rechtsformen („Sonstige", „Unbekannt")
  // bewusst leer — beides läuft hier auf denselben Hinweis hinaus.
  const rechtsformFehlt = art === "intern" && !matrixLaedt && matrix.length === 0;

  const hasGf = roles.some((r) => r.kind === "gf");
  const gesRole = roles.find((r) => r.kind === "gesellschafter") as
    | Extract<VerantRole, { kind: "gesellschafter" }>
    | undefined;
  // Inhaltlicher Schlüssel der gesetzten Verantwortlichkeiten. Der Aufrufer baut
  // `value` bei jedem Tastendruck neu — über die Array-REFERENZ liefe der Memo
  // unten bei jedem Zeichen im Anteile-Feld erneut. Getrennt wird mit einem
  // Zeilenumbruch: Bezeichnungen enthalten Leerzeichen, Schrägstriche und
  // Klammern („Teilhaber / Miteigentümer", „Besonderer Vertreter (§ 30 BGB)").
  const menschBezeichnungen = roles.flatMap((r) => (r.kind === "mensch" ? [r.verantwortlichkeit] : []));
  const menschSchluessel = menschBezeichnungen.join("\n");

  const options: RollenOption[] = React.useMemo(() => {
    const basis = baueRollenOptionen({
      matrix,
      katalog: items,
      art,
      gegenkontaktIstFirma,
      matrixBekannt,
    });
    if (loading) return basis;
    return ergaenzeBestandsRollen(
      basis,
      {
        hatGf: hasGf,
        hatGesellschafter: !!gesRole,
        bezeichnungen: menschBezeichnungen,
      },
      items,
    );
    // gesRole hängt nur als „gesetzt/nicht gesetzt" drin — die Anteile ändern die
    // Optionsliste nicht.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matrix, items, art, gegenkontaktIstFirma, matrixBekannt, loading, hasGf, !!gesRole, menschSchluessel]);

  const optionNachWert = React.useMemo(() => new Map(options.map((o) => [o.value, o])), [options]);
  const katalogWerte = React.useMemo(
    () => new Set(options.filter((o) => o.value !== OPT_GF && o.value !== OPT_GES && o.value !== OPT_SONSTIGES).map((o) => o.value)),
    [options],
  );

  // Mensch-Rolle, deren Verantwortlichkeit NICHT in der Optionsliste steht →
  // „Sonstiges". Erst auswerten, wenn geladen ist (sonst Fehlklassifikation).
  const sonstigesRole = loading
    ? undefined
    : (roles.find(
        (r): r is Extract<VerantRole, { kind: "mensch" }> =>
          r.kind === "mensch" && !katalogWerte.has(r.verantwortlichkeit),
      ) ?? undefined);

  // „Sonstiges"-Feld bleibt sichtbar, sobald die Option aktiv ist — auch wenn noch
  // kein Text eingetippt wurde (also noch keine Custom-Rolle existiert).
  const [sonstigesOpen, setSonstigesOpen] = React.useState(false);
  const sonstigesActive = sonstigesOpen || !!sonstigesRole;

  /**
   * Roh-Text des Sonstiges-Feldes. Muss LOKAL gehalten werden: gespeichert wird
   * getrimmt, und käme der Feldwert wie früher aus der Rolle zurück, verschwände
   * jedes gerade getippte Leerzeichen sofort wieder (aus „Leiter Einkauf" wurde
   * „LeiterEinkauf"). Von außen (Laden, Typwechsel) wird nachgezogen, solange
   * nicht nur Rand-Leerraum den Unterschied macht.
   */
  const externerSonstigesText = sonstigesRole?.verantwortlichkeit ?? "";
  const [sonstigesText, setSonstigesText] = React.useState(externerSonstigesText);
  React.useEffect(() => {
    setSonstigesText((cur) => (cur.trim() === externerSonstigesText ? cur : externerSonstigesText));
  }, [externerSonstigesText]);

  // Aktuelle Auswahl aus den Rollen ableiten.
  const selectedOptions = React.useMemo(() => {
    const sel: string[] = [];
    if (hasGf) sel.push(OPT_GF);
    if (gesRole) sel.push(OPT_GES);
    for (const r of roles) {
      if (r.kind === "mensch" && katalogWerte.has(r.verantwortlichkeit)) sel.push(r.verantwortlichkeit);
    }
    // Bestand: „Gesellschafter + geschäftsführend" ist EINE Zeile und meint die
    // Kombination. Ohne diese Auflösung stünde die Leitung nach dem Öffnen nicht
    // mehr in der Auswahl und fiele beim Speichern weg.
    if (gesRole?.istGeschaeftsfuehrer && !hasGf) {
      const leitung = leitungsOptionFuerBestand(options);
      if (leitung && !sel.includes(leitung)) sel.push(leitung);
    }
    if (sonstigesActive) sel.push(OPT_SONSTIGES);
    return sel;
  }, [roles, hasGf, gesRole, katalogWerte, sonstigesActive, options]);

  // ── Haken „Mensch im Unternehmen" ────────────────────────────────────────
  // Ein extern beauftragter Dienstleister arbeitet nicht im Unternehmen, und
  // eine beteiligte Firma ist kein Mensch — in beiden Fällen entfällt der Haken
  // ganz (dieselbe Grenze zieht das Backend beim Status „Mensch fürs
  // Unternehmen", ansprechpartner-cleanup.service.ts).
  const hakenMoeglich = art === "intern" && !gegenkontaktIstFirma;
  const hakenJeRolle = React.useMemo(
    () => baueHakenJeRolle(items, matrix, optionsWert),
    [items, matrix],
  );
  const hakenFuer = (nextRoles: VerantRole[]) =>
    menschImUnternehmenWert(
      {
        roles: nextRoles,
        hakenMoeglich,
        istMenschImUnternehmen: value?.istMenschImUnternehmen,
        istMenschImUnternehmenManuell: value?.istMenschImUnternehmenManuell,
      },
      hakenJeRolle,
    );
  // A8: Vorschlag für eine neue Verantwortung, solange niemand den Haken
  // selbst gesetzt hat (beim Öffnen oder per Klick).
  const startManuell = React.useRef(!!value?.istMenschImUnternehmenManuell);
  const [handGesetzt, setHandGesetzt] = React.useState(false);
  const vorschlagNeu = !!neuerEintrag && !startManuell.current && !handGesetzt;
  const hakenAktuell = (nextRoles: VerantRole[]): boolean =>
    vorschlagNeu
      ? hakenVorschlagNeu(nextRoles, hakenMoeglich, (k) => optionNachWert.get(k))
      : hakenFuer(nextRoles);
  /**
   * Manuell-Kennzeichen zum Haken: Im Vorschlags-Modus genau dann, wenn der
   * Vorschlag von der Katalog-Ableitung abweicht — sonst kippte der Haken beim
   * nächsten Öffnen zurück.
   */
  const manuellAktuell = (nextRoles: VerantRole[]): boolean =>
    vorschlagNeu
      ? hakenAktuell(nextRoles) !== abgeleiteterHaken({ roles: nextRoles, hakenMoeglich }, hakenJeRolle)
      : !!value?.istMenschImUnternehmenManuell;
  const haken = hakenAktuell(roles);
  const manuell = manuellAktuell(roles);

  /**
   * Der abgeleitete Vorschlag muss beim Aufrufer ankommen, ohne dass jemand die
   * Checkbox anfasst. Sonst zeigte das Popup bei einem Geschäftsführer den Haken
   * an, gespeichert würde aber `false` — und der Bestand (`relsToVerant` liefert
   * das Feld gar nicht mit) verlöre ihn bei jedem Öffnen und Speichern.
   * Erst nach dem Laden, weil die Ableitung Katalog UND Matrix braucht.
   */
  React.useEffect(() => {
    if (!value || loading) return;
    // Ein noch nie gesetztes Feld ist fachlich „aus" — sonst löste jeder frische
    // Eintrag eine überflüssige Runde durch den Aufrufer aus.
    const manuellPasst = !vorschlagNeu || !!value.istMenschImUnternehmenManuell === manuell;
    if (!!value.istMenschImUnternehmen === haken && manuellPasst) return;
    onChange({
      ...value,
      istMenschImUnternehmen: haken,
      ...(vorschlagNeu ? { istMenschImUnternehmenManuell: manuell } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, haken, manuell, vorschlagNeu, value?.istMenschImUnternehmen, value?.istMenschImUnternehmenManuell]);

  const emit = (nextRoles: VerantRole[]) =>
    onChange({
      isExtern: value?.isExtern ?? false,
      roles: nextRoles,
      externalCompany: value?.externalCompany ?? null,
      // Die Assistenz überlebt jeden Rollenwechsel: Sie hängt an der PERSON,
      // nicht an einer einzelnen Rolle. Ob der Abschnitt sichtbar BLEIBT,
      // entscheidet die Kategorie — das ist eine Frage der Anzeige, nicht des
      // Werts. Würde hier genullt, verschwände eine eben eingetippte Assistenz
      // beim Anhaken einer zweiten Rolle.
      assistenz: value?.assistenz ?? null,
      // Rollen ändern die Ableitung — der Haken muss im selben Zug nachziehen.
      istMenschImUnternehmen: hakenAktuell(nextRoles),
      istMenschImUnternehmenManuell: manuellAktuell(nextRoles),
    });

  /**
   * Baut die Rollen aus einer Options-Menge neu (Anteile/Freitext erhalten).
   *
   * `istGeschaeftsfuehrer` wird nicht mehr gewählt, sondern ABGELEITET: Leitung
   * + Beteiligung an derselben Verbindung ergeben den geschäftsführenden
   * Gesellschafter (PO 15.08.2026). Das Feld bleibt gesetzt, weil daran im
   * Bestand `function='geschäftsführend'` hängt.
   */
  const rolesFromOptions = (opts: Set<string>, freitext: string): VerantRole[] => {
    const gewaehlteKategorien = Array.from(opts, (v) => optionNachWert.get(v)?.kategorie ?? "WEITERE");
    const gfGes = istGfGesellschafter(gewaehlteKategorien);
    const next: VerantRole[] = [];
    if (opts.has(OPT_GF)) next.push({ kind: "gf" });
    if (opts.has(OPT_GES)) next.push({ kind: "gesellschafter", share: gesRole?.share, istGeschaeftsfuehrer: gfGes });
    // Anteile je Rolle erhalten: Seit 08/2026 trägt nicht nur der Gesellschafter
    // eine Quote (Kommanditist, Wohnungseigentümer, Aktionär). Ohne diese
    // Zuordnung fiele der eingetippte Wert beim nächsten Klick in der Liste weg.
    const shareJeRolle = new Map(
      roles.flatMap((r) => (r.kind === "mensch" ? ([[r.verantwortlichkeit, r.share] as const]) : [])),
    );
    for (const o of options) {
      if (o.value === OPT_GF || o.value === OPT_GES || o.value === OPT_SONSTIGES) continue;
      if (opts.has(o.value))
        next.push({ kind: "mensch", verantwortlichkeit: o.value, share: shareJeRolle.get(o.value) });
    }
    // Gespeichert wird ohne Rand-Leerraum; das EINGABEFELD hält seinen eigenen
    // State (siehe oben), sonst würde dieser trim jedes getippte Leerzeichen
    // sofort wieder wegschneiden — „Leiter Einkauf" wurde so zu „LeiterEinkauf".
    const t = freitext.trim();
    if (opts.has(OPT_SONSTIGES) && t)
      next.push({ kind: "mensch", verantwortlichkeit: t, share: shareJeRolle.get(t) });
    return next;
  };

  const applySelection = (next: string[]) => {
    const effective = new Set(next);
    const sonstigesNowOn = effective.has(OPT_SONSTIGES);
    setSonstigesOpen(sonstigesNowOn);
    if (!sonstigesNowOn) setSonstigesText("");
    emit(rolesFromOptions(effective, sonstigesNowOn ? sonstigesText : ""));
  };

  const onSonstigesText = (text: string) => {
    const opts = new Set(selectedOptions);
    opts.add(OPT_SONSTIGES);
    setSonstigesOpen(true);
    setSonstigesText(text);
    emit(rolesFromOptions(opts, text));
  };

  const setArt = (a: "intern" | "extern") => {
    if (a === art) return;
    // Typwechsel: Rollen sind typ-spezifisch → leeren (Firma nur bei extern).
    // Ohne Rückfrage; die Verantwortlichkeit-Auswahl wird einfach geleert.
    setSonstigesOpen(false);
    setSonstigesText("");
    // Mit den Rollen fällt auch der Haken zurück auf die Ableitung.
    onChange({
      isExtern: a === "extern",
      roles: [],
      externalCompany: null,
      // Hier fallen ALLE Rollen — damit auch der Anspruch auf eine Assistenz.
      // Sie stehen zu lassen hieße, sie beim Speichern unsichtbar mitzuschicken.
      assistenz: null,
      istMenschImUnternehmen: false,
      istMenschImUnternehmenManuell: false,
    });
  };

  /**
   * Anteil EINER Rolle setzen. `optionsWert` ist der Schlüssel: OPT_GES trifft die
   * Gesellschafter-Rolle, jeder andere Wert die gleichnamige „mensch"-Rolle.
   */
  const setzeAnteil = (optionsWertDerRolle: string, share: string) =>
    emit(
      roles.map((r) => {
        if (optionsWertDerRolle === OPT_GES) return r.kind === "gesellschafter" ? { ...r, share } : r;
        return r.kind === "mensch" && r.verantwortlichkeit === optionsWertDerRolle ? { ...r, share } : r;
      }),
    );

  /** Der aktuell gespeicherte Anteil einer Rolle (für den Feldwert). */
  const anteilVon = (optionsWertDerRolle: string): string => {
    if (optionsWertDerRolle === OPT_GES) return gesRole?.share ?? "";
    const r = roles.find((x) => x.kind === "mensch" && x.verantwortlichkeit === optionsWertDerRolle);
    return (r?.kind === "mensch" ? r.share : undefined) ?? "";
  };

  const onExtCompany = (ref: ContactRef | null) =>
    onChange({
      isExtern: value?.isExtern ?? false,
      roles: value?.roles ?? [],
      externalCompany: ref,
      assistenz: value?.assistenz ?? null,
      istMenschImUnternehmen: haken,
      istMenschImUnternehmenManuell: manuell,
    });

  // Abgeleitete Anzeige statt eigener Option: Leitung + Beteiligung.
  const gewaehlteKategorien = selectedOptions.map(
    (v) => optionNachWert.get(v)?.kategorie ?? "WEITERE",
  );
  const zeigeGfGesellschafter = istGfGesellschafter(gewaehlteKategorien);

  return (
    <div className={s.wrap}>
      {/* Typ — bei einer Firma als Gegenkontakt fest „intern", daher ausgeblendet */}
      {!gegenkontaktIstFirma && (
        <div className={s.field}>
          <span className={s.fieldLbl}>Typ</span>
          <div className={s.seg}>
            {(["intern", "extern"] as const).map((a) => (
              <button
                key={a}
                type="button"
                className={mergeClasses(s.segBtn, art === a && s.segActive)}
                onClick={() => setArt(a)}
                id={`${idBase}-art-${a}`}
              >
                {a === "intern" ? "Intern" : "Extern"}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Rechtsform fehlt → die Organrollen stehen nicht fest */}
      {rechtsformFehlt && (
        <div className={s.warnBox} id={`${idBase}-rechtsform-hinweis`}>
          <span className={s.warnIcon}>{WarnIcon}</span>
          <span>
            {matrixBekannt
              ? "Die Rechtsform dieser Firma ist ein Platzhalter („Sonstige“/„Unbekannt“). Welche Organrollen es gibt, hängt an ihr — bitte zuerst die konkrete Rechtsform erfassen."
              : "Ohne Rechtsform der Firma steht nicht fest, welche Organrollen es gibt (Vorstand, Komplementär, Kommanditist …). Bitte zuerst die Rechtsform erfassen — bis dahin bleibt die Auswahl auf die Grundrollen beschränkt."}
          </span>
        </div>
      )}

      {/* Mehrfachauswahl (Dropdown), nach Kategorien gegliedert */}
      <div className={s.field}>
        <span className={s.fieldLbl}>Verantwortlichkeit(en)</span>
        <VerantwortlichkeitDropdown
          options={options}
          values={selectedOptions}
          onChange={applySelection}
          loading={loading}
          idBase={idBase}
        />
      </div>

      {/* Firma als Gegenüber: Anteile laufen über die Firmenverbindung (A4). */}
      {gegenkontaktIstFirma && (
        <span className={s.hint} id={`${idBase}-firma-anteile`}>
          Hält diese Firma Anteile, wird das unter „Verbindung zu Unternehmen“ erfasst — als Mutterfirma oder als
          Beteiligung.
        </span>
      )}

      {/* „Geschäftsführender Gesellschafter" — abgeleitet, nicht wählbar */}
      {zeigeGfGesellschafter && (
        <span className={s.abgeleitet} id={`${idBase}-gfges`}>
          {LABEL_GF_GESELLSCHAFTER}
        </span>
      )}

      {/* „Mensch im Unternehmen" ist seit 08/2026 wieder wählbar (PO). Die
          Auswahl war am 16.08.2026 entfernt worden mit der Begründung, wer eine
          Verantwortlichkeit trage, arbeite ohnehin im Unternehmen — das stimmt,
          deckt aber den Fall nicht ab, der jetzt gebraucht wird: jemand
          arbeitet dort, OHNE eine Verantwortlichkeit zu tragen. Genau das ist
          der Regelfall, wenn eine Firma über das Arbeitgeber-Feld verknüpft
          wird.

          Der Haken wird weiterhin aus den Rollen abgeleitet; wer ihn von Hand
          setzt, überschreibt die Ableitung dauerhaft — dafür das
          Manuell-Kennzeichen. Ohne dieses Kennzeichen schriebe der Sync-Effekt
          weiter unten die Eingabe im selben Render zurück.

          Bei „extern" und bei einer Firma als Gegenkontakt entfällt die
          Auswahl: Ein beauftragter Steuerberater arbeitet nicht IM Unternehmen,
          und eine Firma ist kein Mensch. Dieselbe Grenze zieht das Backend beim
          Status. */}
      {hakenMoeglich && (
        <label className={s.mitarbeitZeile}>
          <input
            type="checkbox"
            checked={haken}
            onChange={(e) => {
              setHandGesetzt(true);
              onChange({
                ...(value ?? { isExtern: false, roles: [] }),
                istMenschImUnternehmen: e.target.checked,
                istMenschImUnternehmenManuell: true,
              });
            }}
          />
          <span>
            Mensch im Unternehmen
            <span className={s.mitarbeitHinweis}>
              arbeitet dort — Grundlage für betriebliche Vorsorge
            </span>
          </span>
        </label>
      )}

      {/* Anteile — je gewählter Rolle, die laut Matrix eine Quote trägt. Bis
          08/2026 gab es genau EIN Feld, fest am Gesellschafter; Kommanditistin
          (§ 171 HGB, Euro), Wohnungs-/Miteigentümerin (Bruchteil) und Aktionärin
          konnten ihre Quote nicht erfassen. Iteriert wird über `options`, damit
          die Reihenfolge der Auswahlliste folgt. */}
      {options
        .filter((o) => o.quotenTyp !== "KEINE" && selectedOptions.includes(o.value))
        .map((o) => (
          <AnteilFeld
            key={o.value}
            // Der Gesellschafter behält seine bisherige id — bestehende
            // Selektoren und Tests greifen weiter.
            id={o.value === OPT_GES ? `${idBase}-share` : `${idBase}-share-${slugFuerId(o.value)}`}
            rollenLabel={o.label}
            quotenTyp={o.quotenTyp}
            value={anteilVon(o.value)}
            onChange={(v) => setzeAnteil(o.value, v)}
          />
        ))}

      {/* Sonstiges — Freitext (wird KEINE Dropdown-Option) */}
      {sonstigesActive && (
        <div className={s.field}>
          <span className={s.fieldLbl}>{LABEL_SONSTIGES} (Freitext)</span>
          <input
            className={s.input}
            placeholder="Verantwortlichkeit eintippen …"
            value={sonstigesText}
            onChange={(e) => onSonstigesText(e.target.value)}
            id={`${idBase}-sonstiges`}
          />
        </div>
      )}

      {/* Externe Firma — sichtbar, sobald „Extern" gewählt ist (außer ausgeblendet) */}
      {art === "extern" && !hideExternalCompany && (
        <div className={s.extWrap}>
          <span className={s.label}>Externe Firma</span>
          <span className={s.hint}>Optional — beim Neuanlegen sind Branche, Rechtsform, Telefon und E-Mail Pflicht.</span>
          <ContactRefField
            wantType="Firma"
            firmaExtraFields
            idBase={`${idBase}-extco`}
            value={value?.externalCompany ?? null}
            onChange={onExtCompany}
          />
        </div>
      )}
    </div>
  );
}
